/** Store del formulario "Mis datos": un ModelInputs + metadatos de guardado. Las acciones son puras (acciones.ts). */
import { create } from "zustand";
import type { ModelInputs } from "@/engine/types";
import { emptyInputs } from "@/engine/versions";
import type { Accion } from "./acciones";
import type { DraftSource } from "@/types/db";

export type EstadoGuardado = "idle" | "pendiente" | "guardando" | "guardado" | "error" | "offline" | "local";
export interface SeedUpload { fileName?: string | null; sha256?: string | null; bytes?: number | null; sheetNames?: string[]; warnings?: string[] }

export interface MetaDatos {
  modo: "real" | "demo";
  source: DraftSource;
  seedSnapshotId: string | null;
  seedUpload: SeedUpload | null;
  /** Se cuenta por cada cambio del miembro (no por los de sembrado). */
  revisionLocal: number;
  revisionGuardada: number;
  guardado: EstadoGuardado;
  lastSavedAt: string | null;
  updatedAt: string | null;
  error: string | null;
  cargado: boolean;
  /** Miembro para el que se sembró: si cambia la sesión sin recargar, se vuelve a sembrar (revisión de Diego #7). */
  cargadoPara: string | null;
  avisos: string[];
}

interface Estado {
  inputs: ModelInputs;
  meta: MetaDatos;
  apply: (a: Accion) => void;
  seed: (inputs: ModelInputs, meta: Partial<MetaDatos>) => void;
  reset: (modo?: "real" | "demo") => void;
  setMeta: (m: Partial<MetaDatos>) => void;
}

const metaInicial = (modo: "real" | "demo" = "real"): MetaDatos => ({
  modo, source: "form", seedSnapshotId: null, seedUpload: null, revisionLocal: 0, revisionGuardada: 0,
  guardado: "idle", lastSavedAt: null, updatedAt: null, error: null, cargado: false, cargadoPara: null, avisos: [],
});

export const useDatos = create<Estado>((set) => ({
  inputs: emptyInputs(),
  meta: metaInicial(),
  apply: (a) => set((s) => {
    const inputs = a(s.inputs);
    if (inputs === s.inputs) return s;
    return { inputs, meta: { ...s.meta, revisionLocal: s.meta.revisionLocal + 1, guardado: s.meta.modo === "demo" ? "idle" : "pendiente", updatedAt: new Date().toISOString() } };
  }),
  seed: (inputs, meta) => set((s) => ({ inputs, meta: { ...s.meta, ...meta, cargado: true } })),
  reset: (modo = "real") => set({ inputs: emptyInputs(), meta: metaInicial(modo) }),
  setMeta: (m) => set((s) => ({ meta: { ...s.meta, ...m } })),
}));

/** Hay cambios sin mandar al servidor. */
export const hayPendientes = (m: MetaDatos) => m.revisionLocal > m.revisionGuardada;
